import React, { useEffect, useMemo, useState } from 'react';
import { createCoupon, generateCode } from '../../../api/adminCoupons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// 日本語: フロント側フォールバック用の簡易コード生成
const genLocalCode = (prefix?: string, length: number = 8) => {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // 紛らわしい文字(I/O/1/0)を除外
  let core = '';
  for (let i = 0; i < length; i++) core += alphabet[Math.floor(Math.random() * alphabet.length)];
  return prefix ? `${prefix}-${core}` : core;
};

type DiscountType = '' | 'percent' | 'amount' | 'fixed_price';
type CommissionType = '' | 'percent' | 'amount';

// 日本語: 期間モード（無期限／月数指定）
type PeriodMode = 'unlimited' | 'months';

// 日本語: バリデーション用の正規表現
const CODE_RE = /^[A-Z0-9-]{4,32}$/;        // クーポンコード: A–Z/0–9/-(4〜32)
const LINK_CODE_RE = /^[a-zA-Z0-9]{4,24}$/;  // リンクコード: a–z/A–Z/0–9(4〜24)

// 日本語: 月数加算の終了日時（23:59:59.999）を ISO で返す
const addMonthsEndOfDayISO = (start: Date, months: number) => {
  const d = new Date(start);
  const origDate = d.getDate();
  d.setMonth(d.getMonth() + months);
  // 端数（31日→翌月末 等）のケア
  if (d.getDate() !== origDate) {
    // 翌月に溢れた場合は月初→前日に戻して「月末」に合わせる
    d.setDate(0);
  }
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
};

const AdminCouponCreate: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // —— 権限ガード（直リンクの抑止）——
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token) navigate('/login/admin');
    else if (role !== 'admin') {
      alert(t('admin.common_access_denied') || 'アクセス権限がありません');
      navigate('/');
    }
  }, [navigate, t]);

  // —— フォーム状態 —— 
  const [name, setName] = useState('');
  const [code, setCode] = useState('');         // 表示は大文字、保存時に小文字化（後端）
  const [linkCode, setLinkCode] = useState(''); // 空ならサーバーが自動生成

  const [discountType, setDiscountType] = useState<DiscountType>('percent');
  const [discountValue, setDiscountValue] = useState<number | ''>('');

  const [commissionType, setCommissionType] = useState<CommissionType>('');
  const [commissionValue, setCommissionValue] = useState<number | ''>('');

  // 日本語: 期間（無期限 or 月数指定）
  const [periodMode, setPeriodMode] = useState<PeriodMode>('unlimited');
  const [periodMonths, setPeriodMonths] = useState<1 | 3 | 6 | 12>(3);

  // 日本語: 利用回数制限
  const [maxTotal, setMaxTotal] = useState<number | ''>('');      // 全体での最大利用回数
  const [maxPerUser, setMaxPerUser] = useState<number | ''>('');  // 1ユーザーあたりの最大利用回数

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [genLoading, setGenLoading] = useState(false);

  // —— 入力バリデーション（割引）——
  const discountError = useMemo(() => {
    if (!discountType) return null;
    if (discountValue === '') return t('admin.coupons.errors.discount_required') as string;
    const v = Number(discountValue);
    if (Number.isNaN(v)) return t('admin.coupons.errors.number_required') as string;
    if (discountType === 'percent' && (v < 0 || v > 100)) return t('admin.coupons.errors.percent_range') as string;
    if ((discountType === 'amount' || discountType === 'fixed_price') && v < 0) return t('admin.coupons.errors.non_negative') as string;
    return null;
  }, [discountType, discountValue, t]);

  // —— 入力バリデーション（返金/返佣）——
  const commissionError = useMemo(() => {
    if (!commissionType) return null;
    if (commissionValue === '') return t('admin.coupons.errors.commission_required') as string;
    const v = Number(commissionValue);
    if (Number.isNaN(v)) return t('admin.coupons.errors.number_required') as string;
    if (commissionType === 'percent' && (v < 0 || v > 100)) return t('admin.coupons.errors.percent_range') as string;
    if (commissionType === 'amount' && v < 0) return t('admin.coupons.errors.non_negative') as string;
    return null;
  }, [commissionType, commissionValue, t]);

  // —— 入力バリデーション（コード類）——
  const codeError = useMemo(() => {
    if (!code.trim()) return t('admin.coupons.errors.number_required') as string;
    if (!CODE_RE.test(code.trim().toUpperCase())) return t('admin.coupons.errors.code_format') as string;
    return null;
  }, [code, t]);

  const linkCodeError = useMemo(() => {
    if (!linkCode) return null; // 任意
    if (!LINK_CODE_RE.test(linkCode.trim())) return t('admin.coupons.errors.link_code_format') as string;
    return null;
  }, [linkCode, t]);

  // —— 入力バリデーション（利用回数）——
  const maxTotalError = useMemo(() => {
    if (maxTotal === '' || maxTotal === null) return null;
    const v = Number(maxTotal);
    if (Number.isNaN(v) || !Number.isInteger(v) || v <= 0) {
      return t('admin.coupons.errors.max_total_invalid') || '1以上の整数を入力してください';
    }
    return null;
  }, [maxTotal, t]);

  const maxPerUserError = useMemo(() => {
    if (maxPerUser === '' || maxPerUser === null) return null;
    const v = Number(maxPerUser);
    if (Number.isNaN(v) || !Number.isInteger(v) || v <= 0) {
      return t('admin.coupons.errors.max_per_user_invalid') || '1以上の整数を入力してください';
    }
    return null;
  }, [maxPerUser, t]);

  const disableSubmit =
    saving ||
    !!discountError ||
    !!commissionError ||
    !!codeError ||
    !!linkCodeError ||
    !!maxTotalError ||
    !!maxPerUserError ||
    !name.trim() ||
    !code.trim();

  // —— サーバーでコード自動生成（失敗→ローカル）——
  const handleGenerate = async () => {
    setGenLoading(true);
    try {
      const r = await generateCode();
      if (!r?.code) throw new Error('invalid_response');
      setCode(String(r.code).toUpperCase());
    } catch (e: any) {
      console.error('generate-code failed:', e?.response?.status, e?.response?.data || e);
      const local = genLocalCode(undefined, 8);
      setCode(local);
      alert(t('admin.coupons.fallback_generated') || 'サーバーで生成に失敗したため、ローカルで仮コードを生成しました。');
    } finally {
      setGenLoading(false);
    }
  };

  // —— 送信 —— 
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);

    if (discountError || commissionError || codeError || linkCodeError || maxTotalError || maxPerUserError) return;

    // 日本語: 割引・返金の両方が未設定なら確認（仕様上は許可するが誤操作防止）
    if (!discountType && !commissionType) {
      const ok = window.confirm(t('admin.coupons.errors.no_discount_confirm'));
      if (!ok) return;
    }

    // 日本語: 期間（starts_at / ends_at）を算出
    let starts_at: string | null = null;
    let ends_at: string | null = null;
    if (periodMode === 'months') {
      const now = new Date();
      starts_at = now.toISOString(); // 即時有効
      ends_at = addMonthsEndOfDayISO(now, periodMonths);
    }

    const payload = {
      name: name.trim(),
      description: null,
      code: code.trim(),                            // 後端で lower() される
      link_code: linkCode ? linkCode.trim() : null, // 後端で自動生成（小文字化）
      discount_type: discountType || null,
      discount_value: discountValue === '' ? null : Number(discountValue),
      commission_type: commissionType || null,
      commission_value: commissionValue === '' ? null : Number(commissionValue),
      affiliate_id: null,
      allowed_services: null,
      visit_type_limit: null,
      company_id: null,
      allowed_email_domains: null,
      min_amount: null,
      currency: 'JPY',
      starts_at,
      ends_at,
      // ★ 利用回数
      max_redemptions_total: maxTotal === '' ? null : Number(maxTotal),
      max_redemptions_per_user: maxPerUser === '' ? null : Number(maxPerUser),
      note: null,  // 備考はCreate画面では未使用
      is_active: true,
      stackable: false,
    } as const;

    try {
      setSaving(true);
      await createCoupon(payload as any); // 型が厳しい場合は any でキャスト
      navigate('/admin/coupons');
    } catch (e: any) {
      setErr(e?.response?.data?.detail ?? (t('admin.coupons.create_failed') as string));
    } finally {
      setSaving(false);
    }
  };

  // —— Enterキーでの送信サポート ——（input で Enter → submit）
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const tag = (e.target as HTMLElement).tagName.toLowerCase();
      if (tag !== 'select') {
        e.preventDefault();
        if (!disableSubmit) (document.getElementById('coupon-submit-btn') as HTMLButtonElement)?.click();
      }
    }
  };

  return (
    <div className="w-full min-h-[calc(100vh-64px)] flex items-start justify-center p-6">
      {/* 日本語: 中央寄せのコンテナ */}
      <div className="w-full max-w-xl">
        <h2 className="text-center text-2xl font-semibold mb-6">
          {t('admin.coupons.create_title') || 'クーポン作成'}
        </h2>

        <form onSubmit={handleSubmit} onKeyDown={onKeyDown} className="space-y-4 bg-white rounded-2xl shadow p-6">
          {/* 名称 */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('admin.coupons.fields.name')}</label>
            <input
              className="w-full border rounded px-3 py-2"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('admin.coupons.placeholders.name') || ''}
              required
            />
          </div>

          {/* クーポンコード + 自動生成 */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('admin.coupons.fields.code')}</label>
            <div className="flex gap-2">
              <input
                className={`flex-1 border rounded px-3 py-2 uppercase ${codeError ? 'border-red-500' : ''}`}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder={t('admin.coupons.placeholders.code') || ''}
                required
                inputMode="text"
                pattern="[A-Z0-9-]{4,32}"
                title="A–Z・0–9・-（4〜32文字）"
              />
              <button
                type="button"
                onClick={handleGenerate}
                disabled={genLoading}
                className="px-3 py-2 rounded border hover:bg-gray-50 disabled:opacity-60"
              >
                {genLoading ? t('admin.coupons.actions.generating') : t('admin.coupons.actions.generate')}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('admin.coupons.hints.code_case')}</p>
            {codeError && <p className="text-xs text-red-600 mt-1">{codeError}</p>}
          </div>

          {/* 短縮リンクコード（任意） */}
          <div>
            <label className="block text-sm font-medium mb-1">{t('admin.coupons.fields.link_code')}</label>
            <input
              className={`w-full border rounded px-3 py-2 ${linkCodeError ? 'border-red-500' : ''}`}
              value={linkCode}
              onChange={(e) => setLinkCode(e.target.value)}
              placeholder={t('admin.coupons.placeholders.link_code') || ''}
              inputMode="text"
              pattern="[a-zA-Z0-9]{4,24}"
              title="a–z・A–Z・0–9（4〜24文字）"
            />
            <p className="text-xs text-gray-500 mt-1">{t('admin.coupons.hints.link_auto')}</p>
            {linkCodeError && <p className="text-xs text-red-600 mt-1">{linkCodeError}</p>}
          </div>

          {/* 割引（ユーザー側） */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.coupons.sections.discount')}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.coupons.fields.discount_type')}</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={discountType}
                  onChange={(e) => setDiscountType(e.target.value as DiscountType)}
                >
                  <option value="">{t('admin.coupons.options.none')}</option>
                  <option value="percent">{t('admin.coupons.options.percent')}</option>
                  <option value="amount">{t('admin.coupons.options.amount')}</option>
                  <option value="fixed_price">{t('admin.coupons.options.fixed_price')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.coupons.fields.discount_value')}</label>
                <div className="relative">
                  <input
                    type="number"
                    className="w-full border rounded px-3 py-2 pr-10"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value === '' ? '' : Number(e.target.value))}
                    min={0}
                    step={discountType === 'percent' ? 1 : 100}
                    placeholder={
                      discountType === 'percent'
                        ? (t('admin.coupons.placeholders.discount_percent_example') as string) || '10'
                        : discountType === 'amount'
                          ? '2000'
                          : discountType === 'fixed_price'
                            ? '9800'
                            : ''
                    }
                    disabled={!discountType}
                  />
                  {discountType === 'percent' && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {t('admin.coupons.labels.percent_symbol') || '%'}
                    </span>
                  )}
                </div>
                {discountError && <p className="text-xs text-red-600 mt-1">{discountError}</p>}
              </div>
            </div>
          </div>

          {/* 返金（プロモーション側） */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.coupons.sections.commission')}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.coupons.fields.commission_type')}</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={commissionType}
                  onChange={(e) => setCommissionType(e.target.value as CommissionType)}
                >
                  <option value="">{t('admin.coupons.options.none')}</option>
                  <option value="percent">{t('admin.coupons.options.percent')}</option>
                  <option value="amount">{t('admin.coupons.options.amount')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.coupons.fields.commission_value')}</label>
                <div className="relative">
                  <input
                    type="number"
                    className="w-full border rounded px-3 py-2 pr-10"
                    value={commissionValue}
                    onChange={(e) => setCommissionValue(e.target.value === '' ? '' : Number(e.target.value))}
                    min={0}
                    step={commissionType === 'percent' ? 1 : 100}
                    placeholder={commissionType === 'percent' ? '5' : commissionType === 'amount' ? '500' : ''}
                    disabled={!commissionType}
                  />
                  {commissionType === 'percent' && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {t('admin.coupons.labels.percent_symbol') || '%'}
                    </span>
                  )}
                </div>
                {commissionError && <p className="text-xs text-red-600 mt-1">{commissionError}</p>}
              </div>
            </div>
          </div>

          {/* 利用回数制限 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('admin.coupons.sections.redemptions') || '利用回数制限'}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* 全体での最大利用回数 */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('admin.coupons.fields.max_redemptions_total') || '全体での最大利用回数'}
                </label>
                <input
                  type="number"
                  className="w-full border rounded px-3 py-2"
                  value={maxTotal}
                  onChange={(e) => setMaxTotal(e.target.value === '' ? '' : Number(e.target.value))}
                  min={1}
                  placeholder={t('admin.coupons.placeholders.max_total') || '空欄で制限なし'}
                />
                {maxTotalError && <p className="text-xs text-red-600 mt-1">{maxTotalError}</p>}
              </div>

              {/* 1ユーザーあたりの最大利用回数 */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('admin.coupons.fields.max_redemptions_per_user') || '1ユーザーあたりの最大利用回数'}
                </label>
                <input
                  type="number"
                  className="w-full border rounded px-3 py-2"
                  value={maxPerUser}
                  onChange={(e) => setMaxPerUser(e.target.value === '' ? '' : Number(e.target.value))}
                  min={1}
                  placeholder={t('admin.coupons.placeholders.max_per_user') || '空欄で制限なし'}
                />
                {maxPerUserError && <p className="text-xs text-red-600 mt-1">{maxPerUserError}</p>}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('admin.coupons.hints.redemptions') ||
                '空欄の場合は上限なしとして扱われます。友達紹介など「1回限り」の場合は1を指定してください。'}
            </p>
          </div>

          {/* 期間 */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('admin.coupons.sections.period')}</label>

            <div className="flex flex-wrap items-center gap-3">
              {/* 無期限 */}
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="periodMode"
                  checked={periodMode === 'unlimited'}
                  onChange={() => setPeriodMode('unlimited')}
                />
                <span>{t('admin.coupons.period.unlimited')}</span>
              </label>

              {/* 月数指定 */}
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="periodMode"
                  checked={periodMode === 'months'}
                  onChange={() => setPeriodMode('months')}
                />
                <span>{t('admin.coupons.period.months')}</span>
              </label>

              {periodMode === 'months' && (
                <select
                  className="border rounded px-3 py-2"
                  value={periodMonths}
                  onChange={(e) => setPeriodMonths(Number(e.target.value) as 1 | 3 | 6 | 12)}
                >
                  <option value={1}>{t('admin.coupons.period.m1')}</option>
                  <option value={3}>{t('admin.coupons.period.m3')}</option>
                  <option value={6}>{t('admin.coupons.period.m6')}</option>
                  <option value={12}>{t('admin.coupons.period.m12')}</option>
                </select>
              )}
            </div>

            <p className="text-xs text-gray-500 mt-1">
              {t('admin.coupons.hints.period_hint')}
            </p>
          </div>

          {/* エラー表示 */}
          {err && <div className="text-sm text-red-600">{err}</div>}

          {/* アクション */}
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              id="coupon-submit-btn"
              type="submit"
              disabled={disableSubmit}
              className="px-5 py-2.5 rounded-lg bg-coral text-white hover:opacity-90 disabled:opacity-60"
            >
              {saving ? t('admin.common.saving') : t('admin.common.save')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/coupons')}
              className="px-5 py-2.5 rounded-lg border hover:bg-gray-50"
            >
              {t('admin.common.back') || '戻る'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCouponCreate;
